import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 15;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, polls: 0 });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null, polls: 0 }); return; }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      if (cancelled) return;
      attempt++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, polls: attempt });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ status: "pending", data, polls: attempt });
          return;
        }
        setState({ status: "checking", data, polls: attempt });
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempt >= MAX_POLLS) return setState({ status: "error", data: null, polls: attempt });
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto py-16 px-4">
      <Card>
        <CardContent className="p-8 text-center">
          {state.status === "checking" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
              <p className="font-display uppercase text-2xl mb-2">Confirmation en cours…</p>
              <p className="text-muted-foreground text-sm">Nous validons votre paiement auprès de Stripe. Ne fermez pas cette page.</p>
              <p className="text-xs text-muted-foreground mt-2 font-mono-tech">Tentative {state.polls}/{MAX_POLLS}</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-primary mb-4" />
              <p className="font-display uppercase text-2xl mb-2">Paiement confirmé !</p>
              <p className="text-sm text-muted-foreground mb-2">Merci pour votre soutien à RiverSide Roleplay.</p>
              {state.data?.shop_item_title && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Article :</span> <span className="font-medium">{state.data.shop_item_title}</span>
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-3">Votre avantage sera livré en jeu dans les 30 secondes suivant votre prochaine connexion au serveur (ou immédiatement si vous êtes déjà connecté).</p>
              <Button asChild className="mt-6" data-testid="success-back-shop"><Link to="/boutique-web">Retour à la boutique</Link></Button>
            </>
          )}
          {state.status === "pending" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-display uppercase text-2xl mb-2">En attente de confirmation</p>
              <p className="text-sm text-muted-foreground">Le paiement peut prendre quelques minutes. Vous recevrez votre avantage automatiquement dès que Stripe aura confirmé la transaction.</p>
              <Button asChild variant="outline" className="mt-6"><Link to="/">Retour à l'accueil</Link></Button>
            </>
          )}
          {state.status === "error" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <p className="font-display uppercase text-2xl mb-2">Erreur</p>
              <p className="text-sm text-muted-foreground">Aucune transaction trouvée. Contactez le staff via un ticket si vous avez été débité.</p>
              <Button asChild variant="outline" className="mt-6"><Link to="/tickets/nouveau">Ouvrir un ticket</Link></Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
